import { useState } from "react";
import { C } from "../../shared/theme";
import { hue } from "../../shared/hue";
import { BottomNav } from "../../shared/BottomNav";
import { TopBar } from "../../shared/TopBar";
import { HELP } from "../../shared/helpContent";
import { Tile } from "../../shared/Tile";
import { intro } from "../../shared/textStyles";
import { HOME_TILES } from "../../shared/homeTiles";
import { HOME_STORY_SHOWN } from "../../shared/featureFlags";
import { KIND_ICONS } from "../../shared/kindIcons";
import { visuallyHidden } from "../../shared/visuallyHidden";
import { PAGE_WIDTH, HOME_TILE_MAX } from "../../shared/layout";
import { useWideScreen } from "../../shared/useWideScreen";


// Craig's words (16 Oct 2026): why Nudge exists, and what it is for. Since 20 Oct 2026 each sits folded
// behind a one-line summary (Craig: "too much text above the tiles").
const STORY = [
  { id: "why", title: "Life is hard", summary: "The things that matter most are often the ones we procrastinate on.", hueName: "red",
    icon: <path d="m8 3 4 8 5-5 5 15H2L8 3z" />,
    text: "Getting everything we want usually means doing things that are difficult, uncomfortable, boring, unpleasant, or even painful. We tend to procrastinate on such things and that holds us back from achieving the success we desire." },
  { id: "use", title: "How Nudge helps", summary: "Decide what you want, plan it, and do the hard things.", hueName: "green",
    icon: <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></>,
    text: "Use Nudge to decide what you want your life to look like, build a plan to get there, commit to doing the hard things, and make better decisions with your time." },
];

// Home's tiles grow no taller than they are on a big phone (about 140px at 390×844).
const TILE_MAX_HEIGHT = 140, TILE_GAP = 10, GRID_PADDING_Y = 26;
const GRID_MAX_HEIGHT = Math.ceil(HOME_TILES.length / 2) * (TILE_MAX_HEIGHT + TILE_GAP) - TILE_GAP + GRID_PADDING_Y;

// On a wide screen: square tiles three a row, four from HOME_FOUR_MIN (wideCss), no larger than HOME_TILE_MAX, so they
// never become big blocks around small words (Craig, 24 Oct 2026).
const WIDE_GAP = 14, WIDE_PAD = 32;

// One row of the story card: an icon in its hue, the title and summary, and the full text when opened.
function StoryRow({ row, open, onToggle, first }) {
  const h = hue(row.hueName);
  const panelId = `home-story-${row.id}`;
  return (
    <div style={{ borderTop: first ? "none" : `1px solid ${C.border}` }}>
      <h2 style={{ margin: 0 }}>
        <button onClick={onToggle} aria-expanded={open} aria-controls={open ? panelId : undefined}
          style={{ width: "100%", display: "flex", gap: 12, alignItems: "center", padding: "12px 14px", background: "none", border: "none", textAlign: "left", cursor: "pointer", fontFamily: "inherit", color: C.text }}>
          <span aria-hidden="true" style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 12, display: "grid", placeItems: "center", background: `color-mix(in srgb, ${h.b} 45%, ${C.surface})`, color: h.c }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{row.icon}</svg>
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 17, fontWeight: 800, lineHeight: 1.25 }}>{row.title}</span>
            <span style={{ display: "block", fontSize: 14, fontWeight: 400, lineHeight: 1.4, color: C.textMid, marginTop: 2 }}>{row.summary}</span>
          </span>
          <span aria-hidden="true" style={{ flexShrink: 0, fontSize: 12, color: C.textMid }}>{open ? "▲" : "▼"}</span>
        </button>
      </h2>
      {open && <p id={panelId} style={{ ...intro(), padding: "0 16px 14px 66px" }}>{row.text}</p>}
    </div>
  );
}

// The screen Nudge opens on: why Nudge exists and what it is for, folded into one card (hidden for now:
// HOME_STORY_SHOWN), then one tile per reason to open the app. onOpen(tile) leaves Home for the tile's screen.
export function HomePage({ onOpen, onNav }) {
  const wide = useWideScreen();
  const [openRows, setOpenRows] = useState([]);
  const toggle = (id) => setOpenRows(rows => (rows.includes(id) ? rows.filter(r => r !== id) : [...rows, id]));
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", background: C.bg, color: C.text }}>
      <TopBar logo help={HELP.home} />
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", width: "100%", maxWidth: wide ? "none" : PAGE_WIDTH, margin: "0 auto" }}>
          {HOME_STORY_SHOWN && (
            <div style={{ margin: "4px 16px 0", background: C.surface, border: `1px solid ${C.borderCard}`, borderRadius: 14, overflow: "hidden" }}>
              {STORY.map((row, i) => <StoryRow key={row.id} row={row} first={i === 0} open={openRows.includes(row.id)} onToggle={() => toggle(row.id)} />)}
            </div>
          )}
          {/* No heading above the tiles since 23 Oct 2026 (Craig: remove "Let's conquer procrastination"). */}
          <h1 style={visuallyHidden}>Home</h1>
          {/* The grid fills the height between the bars and its rows stretch to share the room left over, so the tiles
              fill the screen on any phone (Craig, 24 Oct 2026). A row never shrinks below its words: on a short screen
              the page scrolls instead. On a tablet or tall window they stop at a big phone's size, so they never become big
              blocks around small words (Craig, 24 Oct 2026). */}
          <div className={wide ? "home-grid" : undefined} style={wide
            ? { width: "100%", maxWidth: 3 * HOME_TILE_MAX + 2 * WIDE_GAP + 2 * WIDE_PAD, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", alignContent: "start", gap: WIDE_GAP, padding: WIDE_PAD }
            : { flex: 1, maxHeight: GRID_MAX_HEIGHT, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: TILE_GAP, padding: HOME_STORY_SHOWN ? "20px 16px 16px" : "10px 16px 16px" }}>
            {HOME_TILES.map(t => <Tile key={t.id} title={t.title} sub={t.sub} hueName={t.hueName} icon={KIND_ICONS[t.id]} bright={!!t.to} compact={!wide} square={wide} onClick={t.to ? () => onOpen(t) : undefined} />)}
          </div>
        </div>
      </div>
      <BottomNav active="home" onNav={onNav} />
    </div>
  );
}
